import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { lazy, Suspense } from "react";
import ChunkErrorBoundary from "./ChunkErrorBoundary";
import tokenService from "./services/TokenService";

const LoginComponent = lazy(() => import("./new-login"));
const ForgotPassword = lazy(() => import("./new-forgot-password"));
const ResetPasswordForm = lazy(() => import("./new-reset-form"));
const Dashboard = lazy(() => import("./dashboard"));
const Nopage = lazy(() => import("./nopage"));
const LoadingDisplay = lazy(() => import("./loader"));
const SessionWarning = lazy(() => import("./session-warning"));

const WARNING_THRESHOLD = 2 * 60 * 1000; // 2 minutes - fallback warning if proactive refresh fails
const PROACTIVE_REFRESH_THRESHOLD = 60 * 1000; // 60 seconds - trigger silent refresh before expiry
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

const ProtectedRoute = React.memo(({ children, isAuthenticated }) => {
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }
  return children;
});

const PublicRoute = React.memo(({ children, isAuthenticated }) => {
  if (isAuthenticated) {
    const passwordChangeRequired =
      localStorage.getItem("passwordChangeRequired") === "true";
    return (
      <Navigate
        to={passwordChangeRequired ? "/dashboard/reset-password" : "/dashboard"}
        replace
      />
    );
  }
  return children;
});

const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (tokenService.hasValidToken()) {
      return true;
    }
    tokenService.clearAllSessionData();
    return false;
  });

  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showExpiredWarning, setShowExpiredWarning] = useState(false);
  const [showNewVersionWarning, setShowNewVersionWarning] = useState(false); // Add this
  // TokenService.isRefreshing already de-duplicates refresh attempts
  useEffect(() => {
    const checkVersion = async () => {
      try {
        // Skip version check in local development — there is no stable deployed version to compare
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          return;
        }
        const response = await fetch("/version.json?t=" + Date.now());
        // Check if response is OK and is JSON
        if (!response.ok) {
          console.warn("version.json not found, skipping version check");
          return;
        }
        const data = await response.json();
        const storedVersion = localStorage.getItem("appVersion");

        if (storedVersion && storedVersion !== data.version) {
          // New version detected - update stored version and prompt re-login
          console.info("New version detected. Logging out...");
          localStorage.setItem("appVersion", data.version);
          setShowNewVersionWarning(true);
          setTimeRemaining(0);
          setShowExpiredWarning(true);
        } else if (!storedVersion) {
          localStorage.setItem("appVersion", data.version);
        }
      } catch (error) {
        console.error("Version check failed:", error);
      }
    };

    if (isAuthenticated) {
      checkVersion(); // Check on mount
      const interval = setInterval(checkVersion, VERSION_CHECK_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      const remaining = tokenService.getTimeUntilExpiry();
      if (remaining !== null) {
        if (remaining <= 0) {
          setTimeRemaining(0);
          setShowExpiredWarning(true);
        } else {
          setTimeRemaining(remaining);
          setShowExpiredWarning(false);
        }
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    let timer;
    if (isAuthenticated) {
      timer = setInterval(() => {
        const remaining = tokenService.getTimeUntilExpiry();
        if (remaining === null) return; // no expiry info yet

        if (remaining <= 0) {
          setTimeRemaining(0);
          setShowExpiredWarning(true);
        } else if (remaining <= WARNING_THRESHOLD) {
          setTimeRemaining(remaining);
        }
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const checkSession = () => {
      if (isAuthenticated && tokenService.isTokenExpired()) {
        setTimeRemaining(0);
        setShowExpiredWarning(true);
      }
    };

    window.addEventListener("focus", checkSession);
    return () => window.removeEventListener("focus", checkSession);
  }, [isAuthenticated]);

  // Proactive silent token refresh - refresh before WARNING_THRESHOLD kicks in
  useEffect(() => {
    let refreshTimer;
    let checkInterval;

    const scheduleProactiveRefresh = async () => {
      const remaining = tokenService.getTimeUntilExpiry();
      if (remaining === null || !isAuthenticated) return;

      // If token already expired, show warning
      if (remaining <= 0) {
        setTimeRemaining(0);
        setShowExpiredWarning(true);
        return;
      }

      // Calculate when to trigger proactive refresh
      // Refresh when there's PROACTIVE_REFRESH_THRESHOLD + buffer time left
      const refreshBuffer = PROACTIVE_REFRESH_THRESHOLD + 5000; // 65 seconds before expiry
      const timeUntilRefresh = remaining - refreshBuffer;

      if (timeUntilRefresh <= 0 && remaining > 0) {
        // Time to refresh now (within the refresh window but not expired)
        if (!tokenService.isRefreshing && tokenService.hasValidRefreshToken()) {
          console.info("[Container] Initiating proactive silent token refresh...");

          const result = await tokenService.refreshAccessToken();

          if (result.success) {
            // Refresh succeeded - update timeRemaining, reschedule next refresh
            const newRemaining = tokenService.getTimeUntilExpiry();
            setTimeRemaining(newRemaining || 0);
            setShowExpiredWarning(false);
            console.info("[Container] Proactive refresh successful, session extended");
          } else {
            // Refresh failed - if token still valid, show warning as fallback
            const currentRemaining = tokenService.getTimeUntilExpiry();
            if (currentRemaining && currentRemaining > 0) {
              setTimeRemaining(currentRemaining);
              setShowExpiredWarning(false);
              // Modal will show via shouldShowWarning when timeRemaining <= WARNING_THRESHOLD,
              // keeping countdown and "Continue Session" button available for retry
            } else {
              setTimeRemaining(0);
              setShowExpiredWarning(true);
            }
            console.warn("[Container] Proactive refresh failed, showing fallback warning");
          }
        }
      } else if (timeUntilRefresh > 0) {
        // Schedule refresh for later
        if (refreshTimer) clearTimeout(refreshTimer);
        refreshTimer = setTimeout(scheduleProactiveRefresh, Math.min(timeUntilRefresh, 30000));
      }
    };

    if (isAuthenticated) {
      // Initial schedule
      scheduleProactiveRefresh();
      
      // Also check periodically in case timer drifts or tab was inactive
      checkInterval = setInterval(() => {
        const remaining = tokenService.getTimeUntilExpiry();
        if (remaining !== null && remaining <= PROACTIVE_REFRESH_THRESHOLD + 5000 && remaining > 0) {
          scheduleProactiveRefresh();
        }
      }, 30000); // Check every 30 seconds
    }

    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      if (checkInterval) clearInterval(checkInterval);
    };
  }, [isAuthenticated]);

  const handleLoginSuccess = useCallback((loginData) => {
    const { token, tokenExpiryTime, refreshToken = null, refreshTokenExpiryTime = null } = loginData ?? {};

    if (!token) {
      console.error("[Container] Login rejected: no access token provided");
      tokenService.clearAllSessionData();
      return;
    }
    if (!tokenExpiryTime) {
      console.error("[Container] Login rejected: tokenExpiryTime is required");
      tokenService.clearAllSessionData();
      return;
    }

    tokenService.setToken(token, tokenExpiryTime, refreshToken, refreshTokenExpiryTime);

    const remaining = tokenService.getTimeUntilExpiry();
    if (!remaining || remaining <= 0) {
      console.error("[Container] Login rejected: token expiry is invalid or already elapsed");
      tokenService.clearAllSessionData();
      return;
    }

    localStorage.removeItem("appVersion"); // reset so version check re-baselines after login
    setIsAuthenticated(true);
    setTimeRemaining(remaining);
    setShowExpiredWarning(false);
  }, []);

  // ✅ FIX: Wrap in useCallback with empty deps - stable function reference
  const handleLogout = useCallback(() => {
    tokenService.clearAllSessionData();
    setIsAuthenticated(false);
    setTimeRemaining(0);
    setShowExpiredWarning(false);
    setShowNewVersionWarning(false);
  }, []);

  // Handle "Continue Session" button click - attempt manual token refresh
  const handleSessionContinue = useCallback(async () => {
    if (!tokenService.hasValidRefreshToken()) {
      console.warn("[Container] No valid refresh token, cannot continue session");
      return false;
    }

    console.info("[Container] User requested session continuation, attempting refresh...");
    const result = await tokenService.refreshAccessToken();

    if (result.success) {
      const newRemaining = tokenService.getTimeUntilExpiry();
      setTimeRemaining(newRemaining || 0);
      setShowExpiredWarning(false);
      console.info("[Container] Session continued successfully");
      return true;
    } else {
      console.warn("[Container] Session continuation failed");
      // Token refresh failed - could be network issue or invalid refresh token
      // Keep showing the warning, user can try again or logout
      return false;
    }
  }, []);

  return {
    isAuthenticated,
    handleLoginSuccess,
    handleLogout,
    handleSessionContinue,
    timeRemaining,
    showExpiredWarning,
    showNewVersionWarning,
  };
};

const Container = () => {
  const {
    isAuthenticated,
    handleLoginSuccess,
    handleLogout,
    handleSessionContinue,
    timeRemaining,
    showExpiredWarning,
    showNewVersionWarning,
  } = useAuth();

  // Clear chunk-reload guard once the app boots successfully
  useEffect(() => {
    sessionStorage.removeItem("chunk_reload_attempted");
  }, []);

  // ✅ FIX: Memoize routes with stable dependencies
  useEffect(() => {
    const handleUnauthorized = () => handleLogout();

    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'AUTH_EXPIRED') {
        handleLogout();
      }
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      window.removeEventListener('message', handleMessage);
    };
  }, [handleLogout]);

  // ✅ FIX: Memoize routes with stable dependencies
  const routes = useMemo(
    () => [
      {
        path: "/",
        element: (
          <PublicRoute isAuthenticated={isAuthenticated}>
            <LoginComponent onLoginSuccess={handleLoginSuccess} />
          </PublicRoute>
        ),
      },
      {
        path: "/forgot-password/",
        element: (
          <PublicRoute isAuthenticated={isAuthenticated}>
            <ForgotPassword onLoginSuccess={handleLoginSuccess} />
          </PublicRoute>
        ),
      },
      {
        path: "/reset-password/",
        element: (
          <PublicRoute isAuthenticated={isAuthenticated}>
            <ResetPasswordForm
              onLoginSuccess={handleLoginSuccess}
              onLogout={handleLogout}
            />
          </PublicRoute>
        ),
      },
      {
        path: "/dashboard/*",
        element: (
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            <Dashboard onLogout={handleLogout} />
          </ProtectedRoute>
        ),
      },
      {
        path: "*",
        element: <Nopage />,
      },
    ],
    [isAuthenticated, handleLoginSuccess, handleLogout],
  );

  const isExpired = showNewVersionWarning || showExpiredWarning;

  // Check if user can continue session (has valid refresh token)
  const canContinueSession = tokenService.hasValidRefreshToken() && !showNewVersionWarning;

  const shouldShowWarning =
    isExpired ||
    (isAuthenticated && timeRemaining > 0 && timeRemaining <= WARNING_THRESHOLD);

  return (
    <BrowserRouter>
      <ChunkErrorBoundary>
        <Suspense fallback={<LoadingDisplay />}>
          {shouldShowWarning && (
            <SessionWarning
              timeRemaining={timeRemaining}
              onLogout={handleLogout}
              onContinue={handleSessionContinue}
              isExpired={isExpired}
              canContinue={canContinueSession}
              expiredMessage={
                showNewVersionWarning
                  ? "A new version has been deployed. Please log in again to access the latest features."
                  : "Your login session has expired. Please login again."
              }
            />
          )}

          <Routes>
            {routes.map(({ path, element }) => (
              <Route key={path} path={path} element={element} />
            ))}
          </Routes>
        </Suspense>
      </ChunkErrorBoundary>
    </BrowserRouter>
  );
};

export default Container;
